import React from 'react';

import ContentContainer from '../../components/uiHelpers/ContentContainer';
import Authorized from '../../auth/Authorized';
import { SUA_ROLES } from '../../utils/app_constants';
import UploadDriveIMEIcsv from '../../components/DriveDeviceAddition/UploadDriveIMEIcsv';
import PageHeader from '../../components/uiHelpers/pageHeader/PageHeader';

import style from './DeviceAdditionUploadIMEI.module.css';

/**
 * =============================================================================
 * COMPONENT: DeviceAdditionUploadIMEI
 * -----------------------------------------------------------------------------
 * PURPOSE:
 * This component serves as the **entry point UI for adding devices (by IMEI)**
 * to the system, typically used by users with administrative or supply-chain
 * access. It provides:
 *   - Role-based access control via the `Authorized` wrapper.
 *   - A structured page layout using ContentContainer.
 *   - A header for navigation clarity.
 *   - The CSV upload interface for bulk IMEI input (handled by UploadDriveIMEIcsv).
 *
 * =============================================================================
 * STRUCTURE OVERVIEW:
 *
 * 1. <Authorized> — Ensures that only specific user roles can access this page.
 * 2. <ContentContainer> — Provides consistent layout/styling across admin pages.
 * 3. <PageHeader> — Displays a section title (“DEVICE ADDITION”).
 * 4. <UploadDriveIMEIcsv> — The core functional component that handles CSV import,
 *    IMEI parsing, validation, and API upload logic.
 * =============================================================================
 */

function DeviceAdditionUploadIMEI({ match: { url } }) {
  return (
    /**
     * ------------------------------------------------------------------------
     * ROLE VALIDATION WRAPPER
     * ------------------------------------------------------------------------
     * `Authorized` restricts access to users with one of the allowed roles:
     *  - ADMIN
     *  - MOBILE_APP_DEVS
     *  - DRIVE_ADMIN
     *
     * If an unauthorized user attempts to access, an error modal will appear
     * (controlled by `willShowErrorModal`).
     */
    <Authorized
      roles={[
        SUA_ROLES.ADMIN,
        SUA_ROLES.MOBILE_APP_DEVS,
        SUA_ROLES.DRIVE_ADMIN
      ]}
      willShowErrorModal
    >
      {/**
       * --------------------------------------------------------------------
       * PAGE LAYOUT CONTAINER
       * --------------------------------------------------------------------
       * ContentContainer provides standardized padding, margins, and layout
       * consistency across different app pages.
       */}
      <ContentContainer className={style.container}>
        {/**
         * ---------------------------------------------------------------
         * HEADER SECTION
         * ---------------------------------------------------------------
         * Displays the top heading for context: "DEVICE ADDITION".
         * The PageHeader component is a reusable styled header component
         * (with title, icon, or breadcrumb support if needed).
         */}
        <div className={style.header}>
          <PageHeader title={'DEVICE ADDITION'} className={style.heading} />
        </div>

        {/**
         * ---------------------------------------------------------------
         * MAIN CONTENT SECTION
         * ---------------------------------------------------------------
         * Contains the CSV upload interface.
         * The UploadDriveIMEIcsv component handles:
         *   - Uploading a .csv file of device IMEIs.
         *   - Parsing CSV data (via PapaParse).
         *   - Displaying results or errors.
         *   - Submitting validated data to backend.
         */}
        <div className={style.content}>
          <UploadDriveIMEIcsv />
        </div>
      </ContentContainer>
    </Authorized>
  );
}

/**
 * -----------------------------------------------------------------------------
 * DEFAULT EXPORT
 * -----------------------------------------------------------------------------
 * Exports this component for routing use (e.g., admin routes like:
 * `/admin/device-addition`).
 * -----------------------------------------------------------------------------
 */
export default DeviceAdditionUploadIMEI;
